/* =====================================================================
 * DMInvitationCard — 팀 초대 메시지 카드 (수락/거절 버튼 포함)
 * ===================================================================== */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import { Users, Check, X } from 'lucide-react';
import { acceptInvitation, declineInvitation } from '@auw/api-client';
import { Badge } from '@auw/workspace-ui';
import { useDMStore } from './hooks/useDMStore';
import { useDMConversationStore } from '@auw/stores';
import { useTeamStore } from '@auw/stores';

interface Props {
  invitationId: string;
  invitationStatus: string; // 'pending' | 'accepted' | 'declined'
  teamName: string;
  inviterName?: string;
  /** 현재 유저가 초대받은 사람인지 */
  isInvitee: boolean;
  /** metadata 업데이트용 콜백 (옵셔널) */
  onStatusChange?: (newStatus: string) => void;
}

/** 지정 횟수만큼 재시도 (지수 백오프) */
async function withRetry<T>(fn: () => Promise<T>, retries = 3, baseDelay = 500): Promise<T> {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === retries - 1) throw err;
      await new Promise((r) => setTimeout(r, baseDelay * (i + 1)));
    }
  }
  throw new Error('재시도 초과');
}

const DMInvitationCard: React.FC<Props> = ({
  invitationId,
  invitationStatus,
  teamName,
  inviterName,
  isInvitee,
  onStatusChange,
}) => {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState(invitationStatus);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadUnreadCounts = useDMStore((s) => s.loadUnreadCounts);
  const loadConversations = useDMConversationStore((s) => s.loadConversations);
  const loadMyTeams = useTeamStore((s) => s.loadMyTeams);
  const setWorkspaceView = useTeamStore((s) => s.setWorkspaceView);

  const handleAccept = useCallback(async () => {
    if (loading || status !== 'pending') return;
    setLoading(true);
    setError(null);
    try {
      const result = await withRetry(() => acceptInvitation(invitationId));
      setStatus('accepted');
      onStatusChange?.('accepted');
      try {
        await loadMyTeams();
        if (result.team_id) {
          setWorkspaceView(result.team_id);
        }
        await Promise.all([
          loadUnreadCounts({ force: true }),
          loadConversations({ force: true }),
        ]);
      } catch (syncErr) {
        console.error('초대 수락 후 상태 동기화 실패:', syncErr);
      }
    } catch (err) {
      console.error('초대 수락 실패:', err);
      setError(t('dm.acceptFailed'));
    } finally {
      setLoading(false);
    }
  }, [
    invitationId,
    loadConversations,
    loadMyTeams,
    loadUnreadCounts,
    loading,
    onStatusChange,
    setWorkspaceView,
    status,
  ]);

  const handleDecline = useCallback(async () => {
    if (loading || status !== 'pending') return;
    setLoading(true);
    setError(null);
    try {
      await withRetry(() => declineInvitation(invitationId));
      setStatus('declined');
      onStatusChange?.('declined');
    } catch (err) {
      console.error('초대 거절 실패:', err);
      setError(t('dm.declineFailed'));
    } finally {
      setLoading(false);
    }
  }, [invitationId, loading, status, onStatusChange]);

  return (
    <Card>
      <CardHeader>
        <TeamIcon>
          <Users size={16} />
        </TeamIcon>
        <HeaderText>{t('dm.teamInvitation')}</HeaderText>
      </CardHeader>

      <CardBody>
        <TeamName>{teamName}</TeamName>
        {inviterName && (
          <InviterText>{t('dm.invitedBy', { name: inviterName })}</InviterText>
        )}
      </CardBody>

      {isInvitee && status === 'pending' && (
        <ButtonGroup>
          <AcceptBtn onClick={handleAccept} disabled={loading}>
            <Check size={14} />
            {t('dm.accept')}
          </AcceptBtn>
          <DeclineBtn onClick={handleDecline} disabled={loading}>
            <X size={14} />
            {t('dm.decline')}
          </DeclineBtn>
        </ButtonGroup>
      )}

      {status === 'accepted' && (
        <Badge variant="success" size="md" style={{ gap: 4, alignSelf: 'flex-start' }}>
          <Check size={12} />
          {t('dm.accepted')}
        </Badge>
      )}

      {status === 'declined' && (
        <Badge variant="error" size="md" style={{ gap: 4, alignSelf: 'flex-start' }}>
          <X size={12} />
          {t('dm.declined')}
        </Badge>
      )}

      {!isInvitee && status === 'pending' && (
        <Badge variant="warning" size="md" style={{ alignSelf: 'flex-start' }}>
          {t('dm.pendingResponse')}
        </Badge>
      )}

      {error && <ErrorText>{error}</ErrorText>}
    </Card>
  );
};

export default DMInvitationCard;

/* ---- Styled ---- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  min-width: 220px;
  max-width: 300px;
  animation: ${fadeIn} 0.2s ease;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TeamIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#818cf8' : '#6366f1'};
`;

const HeaderText = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TeamName = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const InviterText = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 2px;
`;

const btnBase = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 16px;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:active:not(:disabled) {
    transform: scale(0.97);
  }
`;

const AcceptBtn = styled.button`
  ${btnBase}
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#22c55e' : '#16a34a'};
  color: #fff;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? '#16a34a' : '#15803d'};
  }
`;

const DeclineBtn = styled.button`
  ${btnBase}
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  color: ${({ theme }) => theme.colors.textMuted};

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f87171' : '#dc2626')};
  margin-top: -4px;
`;
